import { createContext, useState, useCallback, useMemo } from 'react'

import { LegalBlockingModal, LegalAcceptanceModal } from '@/components/legal'
import { usePendingAcceptance } from '@/hooks/use-legal-acceptance'
import { useLegalBannerStore } from '@/stores/legal-banner-store'
import { useAuth } from '@/hooks/use-auth'
import type { PendingDocument } from '@/types/legal'

interface LegalAcceptanceContextValue {
  /** Indica se há documentos pendentes de aceite */
  hasPendingAcceptance: boolean
  /** Indica se o usuário está bloqueado (grace period expirou) */
  isBlocked: boolean
  /** Indica se deve mostrar o banner */
  showBanner: boolean
  /** Documentos pendentes */
  documents: PendingDocument[]
  /** Dias restantes do grace period */
  daysRemaining: number | null
  /** Abre o modal de aceite voluntário */
  openAcceptanceModal: () => void
  /** Recarrega o status de pendências */
  refetch: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const LegalAcceptanceContext = createContext<LegalAcceptanceContextValue | null>(null)

const EMPTY_DOCUMENTS: PendingDocument[] = []

interface LegalAcceptanceProviderProps {
  children: React.ReactNode
}

export function LegalAcceptanceProvider({ children }: LegalAcceptanceProviderProps) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { isBannerDismissed, resetBanner } = useLegalBannerStore()

  const {
    data: pendingData,
    isLoading: isPendingLoading,
    refetch,
  } = usePendingAcceptance(isAuthenticated && !isAuthLoading)

  const hasPendingAcceptance = pendingData?.hasPendingAcceptance ?? false
  const isBlocked = pendingData?.isBlocked ?? false
  const documents = pendingData?.documents ?? EMPTY_DOCUMENTS
  // daysRemaining usa o menor valor entre todos os documentos pendentes
  const daysRemaining =
    documents.length > 0 ? Math.min(...documents.map((d) => d.daysRemaining)) : null

  const openAcceptanceModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleAccepted = useCallback(() => {
    resetBanner()
    refetch()
  }, [resetBanner, refetch])

  const handleModalClose = useCallback(
    (open: boolean) => {
      setIsModalOpen(open)

      if (!open) {
        // Resetar o banner para mostrar novamente se ainda houver documentos pendentes
        resetBanner()
        refetch()
      }
    },
    [resetBanner, refetch],
  )

  // Mostrar banner se:
  // - Há pendências
  // - Não está bloqueado (ainda no grace period)
  // - Banner não foi fechado nesta sessão
  // - Modal não está aberto
  const showBanner = hasPendingAcceptance && !isBlocked && !isBannerDismissed && !isModalOpen

  const showBlockingModal = hasPendingAcceptance && isBlocked

  const contextValue = useMemo(
    () => ({
      hasPendingAcceptance,
      isBlocked,
      showBanner,
      documents,
      daysRemaining,
      openAcceptanceModal,
      refetch,
    }),
    [
      hasPendingAcceptance,
      isBlocked,
      showBanner,
      documents,
      daysRemaining,
      openAcceptanceModal,
      refetch,
    ],
  )

  if (isAuthLoading || isPendingLoading) {
    return (
      <LegalAcceptanceContext.Provider value={contextValue}>
        {children}
      </LegalAcceptanceContext.Provider>
    )
  }

  return (
    <LegalAcceptanceContext.Provider value={contextValue}>
      {children}

      {showBlockingModal && (
        <LegalBlockingModal open={true} documents={documents} onAccepted={handleAccepted} />
      )}

      {!showBlockingModal && (
        <LegalAcceptanceModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          documents={documents}
          daysRemaining={daysRemaining}
        />
      )}
    </LegalAcceptanceContext.Provider>
  )
}
